import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_COMPANIES") {
    const companies = action.payload;
    const newCompanies = [];

    companies.forEach((company) => {
      const companyIndex = state.findIndex((u) => u.id === company.id);
      if (companyIndex !== -1) {
        state[companyIndex] = company;
      } else {
        newCompanies.push(company);
      }
    });

    return [...state, ...newCompanies];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  currentCompany: {
    backgroundColor: theme.palette.action.selected,
  },
}));

const SuperAdmin = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, handleLogout } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [companies, dispatch] = useReducer(reducer, []);
  const [switchingCompany, setSwitchingCompany] = useState(false);

  // Verificar permissão de acesso
  useEffect(() => {
    if (user && !user.super) {
      toast.error("Você não tem permissão para acessar esta página");
      history.push("/");
    }
  }, [user, history]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchCompanies = async () => {
        try {
          const { data } = await api.get("/companies", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_COMPANIES", payload: data.companies });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchCompanies();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleSwitchCompany = async (companyId) => {
    setSwitchingCompany(true);
    try {
      const { data } = await api.post("/auth/switch-company", {
        companyId,
      });

      // Salvar novo token no localStorage
      localStorage.setItem("token", JSON.stringify(data.token));
      
      toast.success("Empresa alterada com sucesso! Recarregando...");
      
      // Recarregar a página para aplicar o novo token e contexto
      setTimeout(() => {
        window.location.href = "/";
      }, 800);
    } catch (err) {
      toastError(err);
      setSwitchingCompany(false);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Acessar Empresas (Super Admin)</Title>
        <MainHeader>
          <TextField
            placeholder="Pesquisar empresas"
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
        </MainHeader>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">ID</TableCell>
              <TableCell align="center">Nome</TableCell>
              <TableCell align="center">Email</TableCell>
              <TableCell align="center">Plano</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {companies.map((company) => (
                <TableRow 
                  key={company.id}
                  className={user.companyId === company.id ? classes.currentCompany : ""}
                >
                  <TableCell align="center">{company.id || "-"}</TableCell>
                  <TableCell align="center">{company.name || "-"}</TableCell>
                  <TableCell align="center">{company.email || "-"}</TableCell>
                  <TableCell align="center">
                    {company.plan?.name || "Sem plano"}
                  </TableCell>
                  <TableCell align="center">{company.status || "-"}</TableCell>
                  <TableCell align="center">
                    {user.companyId === company.id ? (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled
                      >
                        Empresa Atual
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<SwapHorizIcon />}
                        onClick={() => handleSwitchCompany(company.id)}
                        disabled={switchingCompany}
                      >
                        Acessar
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={6} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default SuperAdmin;

